import { qcCellRenderer } from '@/components/UI/QCBadge/qcCellRenderer';
import { PLACEHOLDER_VALUE } from '@/constants';
import type { ColDef } from 'ag-grid-enterprise';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import tableStyles from '../../styles/components/table.module.css';

import type { Sample } from './types';

export function buildRunTableColDefs(t: (key: string) => string): ColDef<Sample>[] {
  return [
    { field: 'flow_cell', headerName: t('col_flowcell'), maxWidth: 100, width: 100 },
    { field: 'lane', headerName: t('col_lane'), maxWidth: 80, width: 80 },
    { field: 'well_position', headerName: t('col_well'), maxWidth: 90, width: 90 },
    { field: 'demux_index', headerName: t('col_demux_id'), maxWidth: 100, width: 100 },
    { field: 'name', headerName: t('col_sample'), minWidth: 150 },
    { field: 'library_group', headerName: t('col_library_group'), minWidth: 120 },
    {
      field: 'sample_quality_qc',
      headerName: t('col_sample_quality_qc'),
      cellClass: tableStyles.tableCellCenter,
      cellRenderer: qcCellRenderer,
      maxWidth: 160,
    },
    {
      field: 'decode_qc',
      headerName: t('col_decode_qc'),
      cellClass: tableStyles.tableCellCenter,
      cellRenderer: qcCellRenderer,
      maxWidth: 120,
    },
    {
      field: 'lane_qc',
      headerName: t('col_lane_qc'),
      cellClass: tableStyles.tableCellCenter,
      cellRenderer: qcCellRenderer,
      maxWidth: 110,
    },
    {
      field: 'controls_qc',
      headerName: t('col_controls_qc'),
      cellClass: tableStyles.tableCellCenter,
      cellRenderer: qcCellRenderer,
      maxWidth: 120,
    },
    {
      field: 'warning',
      headerName: t('col_warning'),
      // Raw, a boolean renders as the literal "false"; see SamplesPage.tsx for the peer.
      // null means no warning recorded, which reads the same as false to a user.
      cellRenderer: ({ value }: { value: boolean | null }) =>
        value === true ? t('col_warning_yes') : t('col_warning_no'),
      maxWidth: 110,
    },
    { field: 'sample_group', headerName: t('col_sample_group'), minWidth: 130 },
    { field: 'study_name', headerName: t('col_study'), minWidth: 130 },
    { field: 'org_name', headerName: t('col_organization'), minWidth: 150 },
  ] as ColDef<Sample>[];
}

export function useRunTableColDefs(): ColDef<Sample>[] {
  const { t } = useTranslation('runs', { keyPrefix: 'header' });
  return useMemo(() => buildRunTableColDefs(t as (key: string) => string), [t]);
}

export type SampleType = {
  name: string;
  study_name: string;
  org_name: string;
  well_position: string;
  demux_index: number;
};

const isValidString = (v: unknown): boolean => typeof v === 'string' && v.trim() !== '' && v !== PLACEHOLDER_VALUE;

const isValidDemuxIndex = (v: unknown): boolean => typeof v === 'number' && Number.isInteger(v) && v >= 0;

export function areAllSampleFieldsFilled(samples: SampleType[]): boolean {
  if (!Array.isArray(samples) || samples.length === 0) return false;

  return samples.every(
    sample =>
      isValidString(sample.name) &&
      isValidString(sample.study_name) &&
      isValidString(sample.org_name) &&
      isValidString(sample.well_position) &&
      isValidDemuxIndex(sample.demux_index),
  );
}

export function isSampleCountCorrect(samples: ReadonlyArray<unknown>, expectedCount: number): boolean {
  if (!Array.isArray(samples)) return false;
  return samples.length === expectedCount;
}

export type SampleDraftInput = {
  name?: string | null;
  study_name?: string | null;
  org_id?: string | null;
  org_name?: string | null;
  well_position?: string | null;
  demux_index?: number | null;
  flow_cell?: string | null;
  lane?: number | null;
  library_group?: string | null;
  sample_group?: string | null;
};

/**
 * Maps a grid/form sample row to the draft payload shape sent to the backend.
 * A customer row that never had a study picked still carries the grid's placeholder value ('-')
 * in study_name/org_name instead of an empty string, so it must be stripped here — otherwise the
 * backend sees a non-null org_name that doesn't match the run's own org and rejects the whole
 * draft as a cross-tenant sample (POR-367).
 */
export function toSampleDraft(s: SampleDraftInput, orgNameToId: Map<string, string>) {
  const studyName = s.study_name && s.study_name !== PLACEHOLDER_VALUE ? s.study_name : null;
  const orgName = s.org_name && s.org_name !== PLACEHOLDER_VALUE ? s.org_name : null;
  const resolvedOrgId = s.org_id || (orgName ? (orgNameToId.get(orgName) ?? null) : null);
  // `sample_type`/`is_control` are omitted because SampleCreateDraft rejects them outright
  // (additionalProperties: false): the server types each sample by matching its plate
  // coordinate against the assay's control layout.
  return {
    name: s.name || null,
    study_name: studyName,
    org_id: resolvedOrgId,
    org_name: resolvedOrgId ? null : orgName,
    well_position: s.well_position || null,
    demux_index: s.demux_index ?? null,
    flow_cell: s.flow_cell ?? null,
    lane: s.lane ?? null,
    library_group: s.library_group ?? null,
    sample_group: s.sample_group ?? null,
  };
}
